import { useState, useEffect } from "react";
import { FiDatabase } from "react-icons/fi";
import { useHubSpotPortals } from "../hooks/useHubSpotPortals";
import { portalSettingsApi } from "../services/api";

export default function MemberCaching() {
    const { selectedPortal } = useHubSpotPortals();
    const portalId = selectedPortal?.portal_id;

    const [cachingEnabled, setCachingEnabled] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(false);

    // Load current setting when portal changes
    useEffect(() => {
        if (!portalId) return;
        setLoading(true);
        portalSettingsApi.fetchPortalSettings(portalId)
            .then(result => {
                setCachingEnabled(result?.settings?.owner_fetch_mode === 'cache');
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [portalId]);

    const handleSave = async () => {
        if (!portalId) return;
        setSaving(true);
        setSaved(false);
        try {
            await portalSettingsApi.updatePortalSettings(portalId, {
                ownerFetchMode: cachingEnabled ? 'cache' : 'hubspot',
            });
            if (cachingEnabled) {
                portalSettingsApi.triggerOwnerCacheRefresh(portalId).catch(() => {});
            }
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            console.error('Failed to save member caching setting:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
                <div className="flex items-center mb-1">
                    <FiDatabase className="w-6 h-6 text-gray-600 mr-3" />
                    <h1 className="text-2xl font-bold text-gray-900">Member Caching</h1>
                </div>
                <p className="text-sm text-gray-500 ml-9">Control how Distributely fetches HubSpot owners for your portal.</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
                <div className="flex items-start justify-between gap-8">
                    <div className="flex-1">
                        <h2 className="text-base font-semibold text-gray-900 mb-1">Enable owner caching</h2>
                        <p className="text-sm text-gray-500">
                            When enabled, Distributely serves HubSpot owner data from a local cache instead of calling the HubSpot API on every request.
                            This improves routing performance but means owner availability may take a few minutes to reflect after a change in HubSpot.
                        </p>
                    </div>
                    <div className="flex-shrink-0 pt-1">
                        <button
                            type="button"
                            role="switch"
                            aria-checked={cachingEnabled}
                            onClick={() => setCachingEnabled(v => !v)}
                            disabled={loading || !portalId}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${cachingEnabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${cachingEnabled ? 'translate-x-6' : 'translate-x-1'}`}
                            />
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-end gap-4">
                {saved && (
                    <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Settings saved
                    </span>
                )}
                <button
                    onClick={handleSave}
                    disabled={saving || loading || !portalId}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-2 px-6 rounded-lg transition duration-200 text-sm"
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>
        </div>
    );
}
